export interface DischargePoint {
  percent: number;
  voltage: number;
}

export interface PerformanceData {
  cycleLife: number;
  efficiency: number;
  selfDischargeMonthly: number;
  chargeTime: number;
  dischargePoints: DischargePoint[];
  weightKg: number;
  energyDensity: number;
}

export interface Product {
  id: string;
  name: string;
  category: 'lithium' | 'agm' | 'gel';
  tagline: string;
  description: string;
  voltage: number;
  capacity: number;
  weight: number;
  cca: number;
  dimensions: string;
  price: number;
  image: string;
  features: string[];
  specs: Record<string, string>;
  badge?: string;
  performance: PerformanceData;
}

const lithiumDischarge: DischargePoint[] = [
  { percent: 0, voltage: 13.4 }, { percent: 10, voltage: 13.2 }, { percent: 20, voltage: 13.1 },
  { percent: 30, voltage: 13.0 }, { percent: 40, voltage: 12.9 }, { percent: 50, voltage: 12.8 },
  { percent: 60, voltage: 12.7 }, { percent: 70, voltage: 12.6 }, { percent: 80, voltage: 12.4 },
  { percent: 90, voltage: 12.0 }, { percent: 100, voltage: 10.0 },
];

const agmDischarge: DischargePoint[] = [
  { percent: 0, voltage: 12.8 }, { percent: 10, voltage: 12.6 }, { percent: 20, voltage: 12.4 },
  { percent: 30, voltage: 12.2 }, { percent: 40, voltage: 12.0 }, { percent: 50, voltage: 11.8 },
  { percent: 60, voltage: 11.6 }, { percent: 70, voltage: 11.4 }, { percent: 80, voltage: 11.0 },
  { percent: 90, voltage: 10.5 }, { percent: 100, voltage: 9.5 },
];

const gelDischarge: DischargePoint[] = [
  { percent: 0, voltage: 12.9 }, { percent: 10, voltage: 12.7 }, { percent: 20, voltage: 12.5 },
  { percent: 30, voltage: 12.3 }, { percent: 40, voltage: 12.1 }, { percent: 50, voltage: 11.9 },
  { percent: 60, voltage: 11.7 }, { percent: 70, voltage: 11.5 }, { percent: 80, voltage: 11.2 },
  { percent: 90, voltage: 10.8 }, { percent: 100, voltage: 10.0 },
];

export const products: Product[] = [
  {
    id: 'sr-li-100',
    name: 'Stingray LiFePO4 100Ah',
    category: 'lithium',
    tagline: 'The Ultimate Marine Power Solution',
    description: 'Our flagship lithium iron phosphate battery delivers unmatched performance for serious marine enthusiasts. Built to withstand the harshest saltwater environments with a 10-year design life.',
    voltage: 12,
    capacity: 100,
    weight: 13.6,
    cca: 0,
    dimensions: '12.99" x 6.85" x 8.27"',
    price: 849,
    image: 'https://readdy.ai/api/search-image?query=lithium%20iron%20phosphate%20marine%20battery%20100ah%20black%20sleek%20modern%20design%20on%20clean%20white%20background%20professional%20product%20photography%20high%20contrast%20sharp%20details%20premium%20quality%20battery%20with%20blue%20accents%20and%20stingray%20branding&width=600&height=500&seq=sr-li-100&orientation=landscape',
    badge: 'Best Seller',
    features: ['10-Year Design Life', '2000+ Charge Cycles', 'Built-in BMS Protection', 'Bluetooth Monitoring', 'IP67 Waterproof Rating', 'Drop-in AGM Replacement'],
    specs: { 'Nominal Voltage': '12.8V', 'Capacity': '100Ah', 'Energy': '1280Wh', 'Max Charge Current': '50A', 'Max Discharge Current': '100A', 'Weight': '13.6 lbs', 'Dimensions': '12.99" x 6.85" x 8.27"', 'Operating Temp': '-4\u00b0F to 140\u00b0F', 'Self-Discharge': '<3% per month', 'Warranty': '5 Years' },
    performance: { cycleLife: 2000, efficiency: 98, selfDischargeMonthly: 3, chargeTime: 2.5, weightKg: 6.2, energyDensity: 94, dischargePoints: lithiumDischarge },
  },
  {
    id: 'sr-li-200',
    name: 'Stingray LiFePO4 200Ah',
    category: 'lithium',
    tagline: 'Double the Power, Half the Weight',
    description: 'When you need serious deep-cycle capacity for trolling motors, live wells, and electronics, the 200Ah LiFePO4 delivers all day long without breaking a sweat.',
    voltage: 12,
    capacity: 200,
    weight: 26.5,
    cca: 0,
    dimensions: '20.47" x 9.45" x 8.66"',
    price: 1499,
    image: 'https://readdy.ai/api/search-image?query=large%20lithium%20marine%20battery%20200ah%20black%20premium%20product%20photography%20clean%20white%20background%20professional%20studio%20shot%20high%20end%20battery%20pack%20with%20terminals%20and%20handle%20sharp%20focus%20commercial%20quality&width=600&height=500&seq=sr-li-200&orientation=landscape',
    badge: 'Pro Choice',
    features: ['10-Year Design Life', '2000+ Charge Cycles', 'Dual Built-in BMS', 'Bluetooth Monitoring', 'IP67 Waterproof Rating', 'Parallel/Series Compatible'],
    specs: { 'Nominal Voltage': '12.8V', 'Capacity': '200Ah', 'Energy': '2560Wh', 'Max Charge Current': '100A', 'Max Discharge Current': '200A', 'Weight': '26.5 lbs', 'Dimensions': '20.47" x 9.45" x 8.66"', 'Operating Temp': '-4\u00b0F to 140\u00b0F', 'Self-Discharge': '<3% per month', 'Warranty': '5 Years' },
    performance: { cycleLife: 2000, efficiency: 98, selfDischargeMonthly: 3, chargeTime: 2.5, weightKg: 12.0, energyDensity: 96, dischargePoints: lithiumDischarge },
  },
  {
    id: 'sr-li-50',
    name: 'Stingray LiFePO4 50Ah',
    category: 'lithium',
    tagline: 'Compact Power for Day Trips',
    description: 'Perfect for smaller vessels and kayak fishing setups. The 50Ah LiFePO4 packs serious punch in a lightweight, compact form factor.',
    voltage: 12,
    capacity: 50,
    weight: 7.2,
    cca: 0,
    dimensions: '7.72" x 5.16" x 6.34"',
    price: 449,
    image: 'https://readdy.ai/api/search-image?query=compact%20small%20lithium%20marine%20battery%2050ah%20black%20sleek%20product%20photography%20white%20background%20professional%20studio%20lighting%20premium%20battery%20with%20terminals%20modern%20design%20sharp%20details&width=600&height=500&seq=sr-li-50&orientation=landscape',
    features: ['10-Year Design Life', '2000+ Charge Cycles', 'Built-in BMS Protection', 'IP67 Waterproof Rating', 'Ultra Lightweight', 'Fast Charge Compatible'],
    specs: { 'Nominal Voltage': '12.8V', 'Capacity': '50Ah', 'Energy': '640Wh', 'Max Charge Current': '25A', 'Max Discharge Current': '50A', 'Weight': '7.2 lbs', 'Dimensions': '7.72" x 5.16" x 6.34"', 'Operating Temp': '-4\u00b0F to 140\u00b0F', 'Self-Discharge': '<3% per month', 'Warranty': '5 Years' },
    performance: { cycleLife: 2000, efficiency: 97, selfDischargeMonthly: 3, chargeTime: 2.5, weightKg: 3.3, energyDensity: 88, dischargePoints: lithiumDischarge },
  },
  {
    id: 'sr-agm-100',
    name: 'Stingray AGM 100Ah',
    category: 'agm',
    tagline: 'Reliable Power, Proven Technology',
    description: 'Our AGM series delivers dependable deep-cycle performance at an accessible price point. Maintenance-free, spill-proof, and built for the marine environment.',
    voltage: 12,
    capacity: 100,
    weight: 63.9,
    cca: 750,
    dimensions: '13.11" x 6.77" x 8.86"',
    price: 299,
    image: 'https://readdy.ai/api/search-image?query=AGM%20marine%20deep%20cycle%20battery%20100ah%20dark%20gray%20black%20product%20photography%20clean%20white%20background%20professional%20studio%20shot%20sealed%20lead%20acid%20battery%20with%20terminals%20commercial%20quality%20sharp%20focus&width=600&height=500&seq=sr-agm-100&orientation=landscape',
    badge: 'Best Value',
    features: ['Maintenance-Free', 'Spill-Proof Design', 'Vibration Resistant', 'Deep Cycle Capable', 'Dual Purpose (Start + Deep)', '3-Year Warranty'],
    specs: { 'Nominal Voltage': '12V', 'Capacity': '100Ah', 'CCA': '750A', 'Reserve Capacity': '190 min', 'Weight': '63.9 lbs', 'Dimensions': '13.11" x 6.77" x 8.86"', 'Operating Temp': '-4\u00b0F to 122\u00b0F', 'Self-Discharge': '<3% per month', 'Warranty': '3 Years' },
    performance: { cycleLife: 500, efficiency: 80, selfDischargeMonthly: 5, chargeTime: 8, weightKg: 29.0, energyDensity: 28, dischargePoints: agmDischarge },
  },
  {
    id: 'sr-agm-75',
    name: 'Stingray AGM 75Ah',
    category: 'agm',
    tagline: 'Mid-Range Power for Everyday Boating',
    description: 'The 75Ah AGM strikes the perfect balance between capacity and weight for mid-size vessels. Ideal for pontoon boats, bass boats, and center consoles.',
    voltage: 12,
    capacity: 75,
    weight: 48.5,
    cca: 600,
    dimensions: '10.24" x 6.61" x 8.27"',
    price: 229,
    image: 'https://readdy.ai/api/search-image?query=AGM%20marine%20battery%2075ah%20dark%20charcoal%20gray%20product%20photography%20white%20background%20professional%20studio%20lighting%20sealed%20lead%20acid%20deep%20cycle%20battery%20with%20red%20and%20black%20terminals%20sharp%20commercial%20quality&width=600&height=500&seq=sr-agm-75&orientation=landscape',
    features: ['Maintenance-Free', 'Spill-Proof Design', 'Vibration Resistant', 'Deep Cycle Capable', 'Dual Purpose', '3-Year Warranty'],
    specs: { 'Nominal Voltage': '12V', 'Capacity': '75Ah', 'CCA': '600A', 'Reserve Capacity': '140 min', 'Weight': '48.5 lbs', 'Dimensions': '10.24" x 6.61" x 8.27"', 'Operating Temp': '-4\u00b0F to 122\u00b0F', 'Self-Discharge': '<3% per month', 'Warranty': '3 Years' },
    performance: { cycleLife: 500, efficiency: 80, selfDischargeMonthly: 5, chargeTime: 7, weightKg: 22.0, energyDensity: 27, dischargePoints: agmDischarge },
  },
  {
    id: 'sr-gel-120',
    name: 'Stingray Gel 120Ah',
    category: 'gel',
    tagline: 'Smooth Power for Sensitive Electronics',
    description: 'Gel technology provides ultra-stable voltage output, making it the preferred choice for sensitive marine electronics, fish finders, and navigation systems.',
    voltage: 12,
    capacity: 120,
    weight: 72.8,
    cca: 0,
    dimensions: '13.11" x 6.77" x 8.86"',
    price: 349,
    image: 'https://readdy.ai/api/search-image?query=gel%20marine%20deep%20cycle%20battery%20120ah%20dark%20navy%20blue%20black%20product%20photography%20clean%20white%20background%20professional%20studio%20shot%20sealed%20gel%20battery%20with%20terminals%20premium%20quality%20sharp%20focus%20commercial&width=600&height=500&seq=sr-gel-120&orientation=landscape',
    badge: 'Electronics Safe',
    features: ['Ultra-Stable Voltage', 'Zero Maintenance', 'No Gassing', 'Deep Discharge Recovery', 'Vibration Resistant', '3-Year Warranty'],
    specs: { 'Nominal Voltage': '12V', 'Capacity': '120Ah', 'Reserve Capacity': '220 min', 'Weight': '72.8 lbs', 'Dimensions': '13.11" x 6.77" x 8.86"', 'Operating Temp': '-4\u00b0F to 122\u00b0F', 'Self-Discharge': '<2% per month', 'Warranty': '3 Years' },
    performance: { cycleLife: 700, efficiency: 85, selfDischargeMonthly: 2, chargeTime: 10, weightKg: 33.0, energyDensity: 32, dischargePoints: gelDischarge },
  },
  {
    id: 'sr-gel-80',
    name: 'Stingray Gel 80Ah',
    category: 'gel',
    tagline: 'Reliable Gel Power, Compact Size',
    description: 'The 80Ah Gel battery is perfect for smaller vessels requiring stable, clean power for electronics without the weight penalty of larger units.',
    voltage: 12,
    capacity: 80,
    weight: 52.4,
    cca: 0,
    dimensions: '10.24" x 6.61" x 8.27"',
    price: 259,
    image: 'https://readdy.ai/api/search-image?query=gel%20marine%20battery%2080ah%20dark%20navy%20charcoal%20product%20photography%20white%20background%20professional%20studio%20lighting%20sealed%20gel%20deep%20cycle%20battery%20with%20terminals%20modern%20design%20sharp%20commercial%20quality&width=600&height=500&seq=sr-gel-80&orientation=landscape',
    features: ['Ultra-Stable Voltage', 'Zero Maintenance', 'No Gassing', 'Deep Discharge Recovery', 'Compact Design', '3-Year Warranty'],
    specs: { 'Nominal Voltage': '12V', 'Capacity': '80Ah', 'Reserve Capacity': '150 min', 'Weight': '52.4 lbs', 'Dimensions': '10.24" x 6.61" x 8.27"', 'Operating Temp': '-4\u00b0F to 122\u00b0F', 'Self-Discharge': '<2% per month', 'Warranty': '3 Years' },
    performance: { cycleLife: 700, efficiency: 85, selfDischargeMonthly: 2, chargeTime: 8, weightKg: 23.8, energyDensity: 30, dischargePoints: gelDischarge },
  },
];

export const getProductById = (id: string): Product | undefined =>
  products.find((p) => p.id === id);

export const getProductsByCategory = (category: Product['category']): Product[] =>
  products.filter((p) => p.category === category);
